// frontend/src/pages/admin/AdminEAHub.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'; 
import { Link } from 'react-router-dom';
import { serviceOpsApi } from '../../api/serviceOps';
import { usersApi } from '../../api/users';
import { zonesApi } from '../../api/zones';
import { walletApi } from '../../api/wallet'; 
import { useAuth } from '../../context/AuthContext'; // <--- IMPORTED useAuth
import { 
    FaBuilding, FaUserPlus, FaTools, FaTruck, FaStore, FaCar, 
    FaCheckCircle, FaSpinner, FaMapMarkerAlt, FaPhone, FaTrash, FaTimes, 
    FaUserTie, FaWrench, FaPlus, FaInfoCircle, FaLightbulb, FaArrowRight, FaBookOpen, FaArrowUp, FaEdit, FaBoxes,
    FaUniversity, FaSearch, FaChevronDown, FaUserShield // <--- ADDED FaUserShield
} from 'react-icons/fa';

// --- DYNAMIC CONFIGURATION (CRITICAL CONSTANT) ---
const TAB_CONFIG = {
    station: {
        group: 'Service Ops',
        label: 'EA Service Centers',
        singular: 'Service Center',
        icon: FaBuilding,
        color: 'text-blue-700',
        bg: 'bg-blue-50',
        border: 'border-blue-600',
        buttonBg: 'bg-blue-700 hover:bg-blue-800',
        addButton: 'Add Service Center',
        submitButton: 'Create Station',
        nameLabel: 'Facility Name (e.g. EA Ikeja Hub)',
        emptyMsg: 'No internal service centers established yet.',
        description: "A physical workshop location owned directly by Everything Automotive. Use this to handle high-value repairs internally.",
        workflow: ["Create Station", "Customer books 'Workshop Visit'", "Assign to Station"]
    },
    freelancer: {
        group: 'Service Ops',
        label: 'EA Mechanics',
        singular: 'Mechanic',
        icon: FaWrench,
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        border: 'border-orange-600',
        buttonBg: 'bg-orange-600 hover:bg-orange-700',
        addButton: 'Add Mechanic',
        submitButton: 'Register Mechanic',
        nameLabel: 'Technician Full Name',
        emptyMsg: 'No internal mechanics registered.',
        description: "A mobile mechanic on your payroll who travels to customers. They receive jobs directly via the Provider Dashboard.",
        workflow: ["Register Mechanic", "Customer books 'Home Service'", "Assign to Mechanic"]
    },
    expert: {
        group: 'Service Ops',
        label: 'EA Experts',
        singular: 'Expert',
        icon: FaUserTie,
        color: 'text-purple-600',
        bg: 'bg-purple-50',
        border: 'border-purple-600',
        buttonBg: 'bg-purple-600 hover:bg-purple-700',
        addButton: 'Add Expert',
        submitButton: 'Register Expert',
        nameLabel: 'Expert Name',
        emptyMsg: 'No technical consultants registered.',
        description: "A technical specialist for high-level vehicle inspections. They generate certified reports.",
        workflow: ["Register Expert", "Customer pays for 'Inspection'", "Assign Expert"]
    },
    logistics_partner: {
        group: 'Logistics',
        label: 'EA Logistics',
        singular: 'Rider/Driver',
        icon: FaTruck,
        color: 'text-yellow-700',
        bg: 'bg-yellow-50',
        border: 'border-yellow-600',
        buttonBg: 'bg-yellow-700 hover:bg-yellow-800',
        addButton: 'Add Rider/Driver',
        submitButton: 'Register Rider',
        nameLabel: 'Driver Name',
        emptyMsg: 'No internal riders registered.',
        description: "Company riders/drivers for delivery. Assign them to 'Processing' orders.",
        workflow: ["Register Rider", "Order is 'Processing'", "Assign Driver"]
    },
    parts_seller: {
        group: 'Commerce',
        label: 'EA Stores (Managed)',
        singular: 'Store Branch',
        icon: FaStore,
        color: 'text-green-700',
        bg: 'bg-green-50',
        border: 'border-green-600',
        buttonBg: 'bg-green-700 hover:bg-green-800',
        addButton: 'Add Store Branch', 
        submitButton: 'Create Store Branch',
        nameLabel: 'Branch Name (e.g. EA Parts Alaba)',
        emptyMsg: 'No internal store branches found.',
        description: "A physical retail branch where you hold stock OR a partner store you manage (Agency Model).",
        workflow: ["Create Branch", "Go to Inventory", "Assign Part to Branch"]
    },
    car_dealer: {
        group: 'Commerce',
        label: 'EA Dealerships',
        singular: 'Dealer Branch',
        icon: FaCar,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-600',
        buttonBg: 'bg-red-600 hover:bg-red-700',
        addButton: 'Add Dealer Branch', 
        submitButton: 'Create Dealer Branch',
        nameLabel: 'Showroom Name (e.g. EA Autos VI)',
        emptyMsg: 'No internal dealership branches found.',
        description: "A showroom for direct car sales. Vehicles get the 'Verified by EA' badge.",
        workflow: ["Create Showroom", "Go to Vehicle Marketplace", "Assign Vehicle to Showroom"]
    }
};
// -----------------------------------------------------------------

const initialFormState = {
    type: 'station',
    name: '',
    user_id: '',
    phone: '',
    email: '',
    skills: '',
    location_lga_id: '',
    state: '', 
    address: '',
    is_active: true,
    is_internal: true, 
    is_managed_by_ea: false, 
    // --- FINANCIALS ---
    bank_code: '',
    account_number: '',
    // --- NEW QUICK REGISTER FIELDS ---
    new_manager_name: '',
    new_manager_email: '', 
    new_manager_password: '',
    new_manager_phone: '',
    // --- STORE NAME FIELD ---
    store_name: '' 
};

// --- CONSTANT FOR DEFAULT STORE NAME ---
const MASTER_ADMIN_EMAIL = "esekiegabriel@gmail.com";
const EXCLUSIVE_STORE_NAME = "Everything Automotive Exclusive Store";

function AdminEAHub() {
    const { user } = useAuth(); // <--- Get logged-in Admin user
    const [internalStaff, setInternalStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Refs for Scrolling
    const topRef = useRef(null);
    const contentRef = useRef(null);
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Location Data
    const [lgas, setLgas] = useState([]);
    const [zones, setZones] = useState([]);
    
    const [showMasterToolkit, setShowMasterToolkit] = useState(true); 
    
    // Popover State
    const [hoveredTab, setHoveredTab] = useState(null); 
    const [mobilePopoverTab, setMobilePopoverTab] = useState(null); 

    // Add/Edit Staff Modal
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null); 
    const [formData, setFormData] = useState(initialFormState);
    const [submitting, setSubmitting] = useState(false);

    // Location Data for Form
    const [users, setUsers] = useState([]);
    const [filteredZones, setFilteredZones] = useState([]);
    
    // --- NEW SEARCHABLE DROPDOWN STATE ---
    const [banks, setBanks] = useState([]);
    const [isBankListOpen, setIsBankListOpen] = useState(false);
    const [bankSearchTerm, setBankSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    // -------------------------------------

    const [activeTab, setActiveTab] = useState('station'); 
    
    // --- NEW STORE NAME UI STATE ---
    const [isCustomStoreName, setIsCustomStoreName] = useState(false);

    // --- DEFINING FETCH DATA WITH USECALLBACK (FIXED) ---
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [staffData, lgasData, zonesData, usersData, banksData] = await Promise.all([
                serviceOpsApi.getAllProviders({ is_internal: true }), 
                zonesApi.getAllLgas(),
                zonesApi.getAllZones(),
                usersApi.getAllUsersAdmin(),
                walletApi.getBanks() 
            ]);
            setInternalStaff(staffData);
            setLgas(lgasData);
            setZones(zonesData); 
            setUsers(usersData);
            setBanks(banksData || []);
        } catch (err) {
            console.error("Failed to load hub data", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        
        // --- SCROLL LISTENER ---
        const handleScroll = () => {
            const scrollContainer = document.querySelector('main');
            if (scrollContainer) {
                if (scrollContainer.scrollTop > 300) {
                    setShowScrollTop(true);
                } else {
                    setShowScrollTop(false);
                }
            } else {
                if (window.scrollY > 300) setShowScrollTop(true);
                else setShowScrollTop(false);
            }
        };

        const scrollContainer = document.querySelector('main');
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
        }
        window.addEventListener('scroll', handleScroll); 

        return () => {
            if (scrollContainer) scrollContainer.removeEventListener('scroll', handleScroll);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [fetchData]);
    
    // --- NEW: Close dropdown when clicking outside ---
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsBankListOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    // -------------------------------------------------

    // Filter Zones when LGA changes in form
    useEffect(() => {
        if (formData.lga_id) {
            const relevantZones = zones.filter(z => z.lga_id === formData.lga_id && z.is_active);
            setFilteredZones(relevantZones);
        } else {
            setFilteredZones([]);
        }
    }, [formData.lga_id, zones]);

    // Group LGAs by State for the dropdown
    const groupedLgas = useMemo(() => {
        return lgas.reduce((acc, lga) => {
            if (!lga.state || !lga.lga) return acc;
            const stateKey = lga.state.trim();
            if (!acc[stateKey]) acc[stateKey] = [];
            acc[stateKey].push(lga);
            return acc;
        }, {});
    }, [lgas]);

    // Get LGAs for the selected state
    const availableLgas = formData.state ? groupedLgas[formData.state] || [] : [];

    // --- STATS CALCULATION ---
    const stats = useMemo(() => {
        return {
            service: internalStaff.filter(s => ['station', 'freelancer', 'expert'].includes(s.type)).length,
            commerce: internalStaff.filter(s => ['parts_seller', 'car_dealer'].includes(s.type)).length,
            logistics: internalStaff.filter(s => s.type === 'logistics_partner').length
        };
    }, [internalStaff]);

    // --- UI HANDLERS ---
    const handleTabClick = (key) => {
        setActiveTab(key);
        if (contentRef.current) {
            setTimeout(() => {
                contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    };

    const handleScrollToTop = () => {
        const scrollContainer = document.querySelector('main');
        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const getLgaName = (id) => {
        const lga = lgas.find(l => l.id === id);
        return lga ? lga.lga : 'Unknown';
    };

    // --- MODAL HANDLERS ---
    const handleOpenAddModal = (type) => {
        setEditingId(null);
        setFormData({ 
            ...initialFormState, 
            type: type,
            is_internal: true, 
            is_managed_by_ea: type === 'parts_seller' ? true : false, 
            new_manager_name: '', new_manager_email: '', new_manager_password: '', new_manager_phone: '',
            // --- CRITICAL: Set Default Store Name ---
            store_name: ['parts_seller', 'car_dealer'].includes(type) ? EXCLUSIVE_STORE_NAME : ''
        });
        setIsCustomStoreName(false); 
        setIsAddModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'state') {
            setFormData(prev => ({ ...prev, state: value, lga_id: '', zone_id: '' }));
        } else if (name === 'lga_id') {
            setFormData(prev => ({ ...prev, lga_id: value, zone_id: '' }));
        } else if (name === 'store_name_select') {
            if (value === 'CUSTOM_ENTRY') {
                setIsCustomStoreName(true);
                setFormData(prev => ({ ...prev, store_name: '' }));
            } else {
                setIsCustomStoreName(false);
                setFormData(prev => ({ ...prev, store_name: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };
    
    // --- NEW BANK HANDLERS ---
    const handleSelectBank = (bank) => {
        setFormData(prev => ({
            ...prev,
            bank_code: bank.code,
            // We don't store bank_name in formData, but we need it for display
        }));
        setIsBankListOpen(false);
        setBankSearchTerm('');
    };

    const getBankName = (code) => {
        const bank = banks.find(b => b.code === code);
        return bank ? bank.name : 'Select Bank';
    };

    const filteredBanks = banks.filter(bank => 
        bank.name.toLowerCase().includes(bankSearchTerm.toLowerCase())
    );
    // -------------------------
    
    // --- NEW ADMIN AUTO-FILL LOGIC (FIXED) ---
    const handleLoadAdminProfile = () => {
        if (!user) return;
        
        console.log("DEBUG: Loading Admin Profile...", user);

        // Find the Admin's LGA ID based on their state/lga_id from profile
        const adminLga = lgas.find(l => l.state === user.state && l.id === user.lga_id);
        
        // FIX: Use user.fullName if it exists and is not an empty string. 
        // Fallback to user.full_name (raw API data) just in case, then email prefix.
        const userName = user.fullName?.trim() || user.full_name?.trim() || user.email.split('@')[0];
        
        setFormData(prev => ({
            ...prev,
            // User Account Details
            user_id: user.id, // <--- CRITICAL FIX: Set the user_id so we don't try to register again
            name: userName, 
            email: user.email,
            phone: user.phone || '',
            new_manager_password: 'password123', // Default password for quick setup
            // Location Details
            state: user.state || 'Lagos',
            lga_id: user.lga_id || (adminLga ? adminLga.id : ''),
            address: user.address || '',
            // Store Name (Only for Stores)
            store_name: ['parts_seller', 'car_dealer'].includes(prev.type) ? EXCLUSIVE_STORE_NAME : '',
            // Internal Flag
            is_internal: true,
            is_managed_by_ea: false,
        }));
        setIsCustomStoreName(false);
        alert("Admin profile loaded! System will link this store to your existing account.");
    };
    // ---------------------------------

    const handleAddOrUpdateStaff = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        console.log("DEBUG: Submitting Form Data:", formData);

        try {
            const payload = { ...formData };
            
            // Final check for required fields that are used for Quick Register
            if (!editingId && !payload.user_id && (!payload.email || !payload.phone)) {
                throw new Error("The Store's Email and Phone are required for Quick Registration.");
            }
            if (['parts_seller', 'car_dealer'].includes(payload.type) && !payload.store_name) {
                throw new Error("Store Name is required for this entity type.");
            }

            // 1. Handle User Registration (Only for new entities AND if no user_id selected)
            if (!editingId) {
                console.log("DEBUG: Creating new entity. Current user_id:", payload.user_id);
                
                if (!payload.user_id) {
                    console.log("DEBUG: No user_id found. Registering new user...");
                    // CRITICAL: Use Store's Email/Phone for new user account
                    const new_user_id = await usersApi.registerCustomerAdmin({
                        email: payload.email,
                        password: payload.new_manager_password,
                        full_name: payload.name,
                        phone: payload.phone
                    }).then(res => res.user_id);
                    
                    payload.user_id = new_user_id;
                    console.log("DEBUG: New user registered with ID:", new_user_id);
                } else {
                    console.log("DEBUG: Using existing user_id:", payload.user_id);
                }
            }

            // 2. Create Provider/Store
            const providerPayload = {
                type: payload.type,
                name: payload.name,
                user_id: payload.user_id,
                phone: payload.phone,
                email: payload.email,
                skills: payload.skills.split(',').map(s => s.trim()).filter(s => s !== ''),
                state: payload.state,
                location_lga_id: payload.lga_id,
                zone_id: payload.zone_id || null,
                address: payload.address,
                is_active: payload.is_active,
                is_internal: payload.is_internal,
                is_shop_open: true,
                // Store-specific fields
                ...(payload.type === 'parts_seller' && { store_name: payload.store_name }), 
                ...(payload.type === 'parts_seller' && { is_managed_by_ea: payload.is_managed_by_ea }),
                ...(payload.type === 'parts_seller' && { bank_code: payload.bank_code, account_number: payload.account_number }),
                ...(payload.type === 'car_dealer' && { store_name: payload.store_name }), 
                // Logistics Partner does not need store_name, bank_code, account_number
            };

            console.log("DEBUG: Sending Provider Payload:", providerPayload);

            await serviceOpsApi.createProvider(providerPayload);
            alert(`${TAB_CONFIG[payload.type].singular} Added Successfully!`);
            
            setIsAddModalOpen(false);
            setEditingId(null);
            setFormData(initialFormState);
            fetchData(); 

        } catch (err) {
            console.error("DEBUG: Error in handleAddOrUpdateStaff:", err);
            alert(err.data?.message || err.message || "Failed to save entity.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteStaff = async (id) => {
        if(!window.confirm("PERMANENT ACTION: Remove this internal entity from the system?")) return;
        try {
            await serviceOpsApi.deleteProvider(id);
            setInternalStaff(prev => prev.filter(s => s.id !== id));
        } catch(err) {
            alert("Failed to remove entity.");
        }
    };

    const currentConfig = TAB_CONFIG[activeTab];

    if (loading) return <div className="p-10 text-center"><FaSpinner className="animate-spin text-3xl text-primary" /> Loading Command Center...</div>;

    return (
        <div className="pb-20 relative" ref={topRef}>
            
            {/* --- MASTER TOOLKIT (EXPLAINER) --- */}
            {showMasterToolkit && (
                <div className="bg-blue-600 text-white p-4 md:p-6 rounded-lg shadow-lg mb-8 relative">
                    <button 
                        onClick={() => setShowMasterToolkit(false)} 
                        className="absolute top-2 right-2 md:top-4 md:right-4 text-blue-200 hover:text-white"
                    >
                        <FaTimes />
                    </button>
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                        <div className="bg-white/20 p-3 rounded-full flex-shrink-0 hidden sm:block">
                            <FaLightbulb size={24} />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-xl font-bold mb-2 flex items-center gap-2">
                                <FaLightbulb className="sm:hidden" /> Welcome to the EA Hub Command Center
                            </h2>
                            <p className="text-blue-100 text-sm leading-relaxed max-w-3xl">
                                This is your exclusive dashboard for managing **Internal Operations**. 
                                Use this page to register your own staff, open new branches, and manage your fleet drivers without going through the public application process.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* --- HEADER SECTION --- */}
            <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                    <div className="flex items-center gap-4">
                        <div className="bg-blue-600 text-white p-3 md:p-4 rounded-xl shadow-lg">
                            <FaBuilding size={24} className="md:text-3xl" />
                        </div>
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Internal Operations</h1>
                            <p className="text-gray-500 text-sm">Manage Staff, Stores & Logistics</p>
                        </div>
                    </div>
                    
                    {/* Quick Stats - Responsive Grid */}
                    <div className="grid grid-cols-3 gap-2 md:gap-4 text-sm w-full lg:w-auto">
                        <div className="px-2 py-2 md:px-4 bg-blue-50 rounded-lg border border-blue-100 text-center">
                            <span className="block font-bold text-lg md:text-xl text-blue-700">{stats.service}</span>
                            <span className="text-blue-600 text-[10px] md:text-xs uppercase font-bold">Service Ops</span>
                        </div>
                        <div className="px-2 py-2 md:px-4 bg-green-50 rounded-lg border border-green-100 text-center">
                            <span className="block font-bold text-lg md:text-xl text-green-700">{stats.commerce}</span>
                            <span className="text-green-600 text-[10px] md:text-xs uppercase font-bold">Commerce</span>
                        </div>
                        <div className="px-2 py-2 md:px-4 bg-yellow-50 rounded-lg border border-yellow-100 text-center">
                            <span className="block font-bold text-lg md:text-xl text-yellow-700">{stats.logistics}</span>
                            <span className="text-yellow-600 text-[10px] md:text-xs uppercase font-bold">Logistics</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- PILLAR NAVIGATION (GRID LAYOUT) --- */}
            <div className="mb-8 relative">
                <h3 className="text-xs font-bold text-gray-400 uppercase mb-3 tracking-wider">Select Operational Unit</h3>
                
                {/* GRID: 1 col mobile, 2 col tablet, 3 col desktop */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(TAB_CONFIG).map(([key, config]) => (
                        <div 
                            key={key} 
                            className="relative group w-full"
                            onMouseEnter={() => setHoveredTab(key)} 
                            onMouseLeave={() => setHoveredTab(null)} 
                        >
                            <button 
                                onClick={() => handleTabClick(key)}
                                className={`w-full px-4 py-3 rounded-lg font-bold text-sm flex items-center justify-between transition-all shadow-sm border ${
                                    activeTab === key 
                                    ? `bg-white ${config.color} ${config.border} ring-2 ring-offset-1 ring-opacity-50` 
                                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <config.icon className={`${activeTab === key ? config.color : 'text-gray-400'}`} />
                                    <span>{config.label}</span>
                                </div>

                                {/* Mobile Info Icon Trigger */}
                                <div 
                                    className="md:hidden p-2 text-gray-400 hover:text-blue-500 -mr-2"
                                    onClick={(e) => {
                                        e.stopPropagation(); 
                                        setMobilePopoverTab(key); 
                                    }}
                                >
                                    <FaInfoCircle />
                                </div>
                            </button>

                            {/* --- DESKTOP POPOVER (Hover) --- */}
                            {hoveredTab === key && (
                                <div className="hidden md:block absolute bottom-full left-0 mb-3 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-5 z-50 animate-fadeIn">
                                    <div className="absolute -bottom-2 left-6 w-4 h-4 bg-white border-b border-r border-gray-200 transform rotate-45"></div>
                                    <div className="flex items-center gap-2 mb-2 border-b pb-2">
                                        <FaBookOpen className={config.color} />
                                        <h4 className={`font-bold text-sm ${config.color}`}>{config.singular} Cookbook</h4>
                                    </div>
                                    <p className="text-xs text-gray-700 mb-3 leading-relaxed font-medium">{config.description}</p>
                                    <div className="bg-gray-50 p-3 rounded border border-gray-100">
                                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Operational Workflow:</p>
                                        <ul className="text-[11px] text-gray-700 space-y-1.5">
                                            {config.workflow.map((step, i) => (
                                                <li key={i} className="flex items-start gap-2">
                                                    <FaArrowRight className="mt-0.5 text-gray-400 flex-shrink-0" size={8} />
                                                    <span>{step}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* --- MOBILE POPOVER MODAL (Centered Overlay) --- */}
            {mobilePopoverTab && (
                <div 
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn"
                    onClick={() => setMobilePopoverTab(null)} 
                >
                    <div 
                        className="bg-white w-full max-w-sm p-6 rounded-xl shadow-2xl border border-gray-100 relative"
                        onClick={(e) => e.stopPropagation()} 
                    >
                        <button 
                            onClick={() => setMobilePopoverTab(null)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes size={20} />
                        </button>

                        <div className="flex items-center gap-2 mb-4 border-b pb-3">
                            <FaBookOpen className={TAB_CONFIG[mobilePopoverTab].color} size={20} />
                            <h4 className={`font-bold text-lg ${TAB_CONFIG[mobilePopoverTab].color}`}>{TAB_CONFIG[mobilePopoverTab].singular} Guide</h4>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-5 leading-relaxed">{TAB_CONFIG[mobilePopoverTab].description}</p>
                        
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <p className="text-xs font-bold text-gray-500 uppercase mb-3">Operational Workflow:</p>
                            <ul className="text-sm text-gray-700 space-y-3">
                                {TAB_CONFIG[mobilePopoverTab].workflow.map((step, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <FaArrowRight className="mt-1 text-gray-400 flex-shrink-0" size={12} />
                                        <span>{step}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        
                        <button 
                            onClick={() => setMobilePopoverTab(null)}
                            className="w-full mt-6 bg-gray-100 text-gray-700 font-bold py-3 rounded-lg hover:bg-gray-200"
                        >
                            Close Guide
                        </button>
                    </div>
                </div>
            )}

            {/* --- ACTIVE TAB CONTENT --- */}
            <div className="animate-fadeIn" ref={contentRef}>
                
                {/* Tab Header & Toolkit */}
                <div className={`mb-6 p-4 rounded-lg border ${currentConfig.bg} ${currentConfig.border.replace('border-', 'border-opacity-30 ')}`}>
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                        <div>
                            <h2 className={`text-lg md:text-xl font-bold flex items-center gap-2 ${currentConfig.color}`}>
                                <currentConfig.icon /> {currentConfig.label}
                            </h2>
                            <div className="flex items-start gap-2 mt-2 text-sm text-gray-600">
                                <FaInfoCircle className="mt-1 flex-shrink-0 opacity-50" />
                                <p>
                                    {currentConfig.description}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 mt-2 lg:mt-0 w-full lg:w-auto">
                            {/* Contextual Links */}
                            {activeTab === 'parts_seller' && (
                                <Link 
                                    to={`/admin/inventory?store_id=${internalStaff.find(s => s.type === 'parts_seller')?.id || 'none'}`} 
                                    className="bg-white text-green-700 border border-green-200 px-4 py-2 rounded font-bold flex items-center justify-center gap-2 hover:bg-green-50 shadow-sm text-sm w-full sm:w-auto"
                                >
                                    <FaBoxes /> Manage Inventory
                                </Link>
                            )}
                            {activeTab === 'car_dealer' && (
                                <Link 
                                    to={`/admin/vehicles?store_id=${internalStaff.find(s => s.type === 'car_dealer')?.id || 'none'}`} 
                                    className="bg-white text-red-700 border border-red-200 px-4 py-2 rounded font-bold flex items-center justify-center gap-2 hover:bg-red-50 shadow-sm text-sm w-full sm:w-auto"
                                >
                                    <FaCar /> Manage Vehicles
                                </Link>
                            )}
                            
                            {/* Add Button */}
                            <button 
                                onClick={() => handleOpenAddModal(activeTab)}
                                className={`text-white px-5 py-2 rounded font-bold flex items-center justify-center gap-2 shadow-md hover:opacity-90 transition-opacity w-full sm:w-auto ${currentConfig.buttonBg}`}
                            >
                                <FaPlus /> {currentConfig.addButton}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Grid List */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {internalStaff.filter(s => s.type === activeTab).map(staff => (
                        <div key={staff.id} className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 relative group hover:shadow-md transition-shadow min-w-0 ${!staff.is_active ? 'border-red-200 bg-red-50' : 'border-gray-200'}">
                            <div className="flex items-center gap-4 mb-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${currentConfig.bg} ${currentConfig.color}`}>
                                    {staff.name.charAt(0)}
                                </div>
                                <div className="min-w-0 overflow-hidden">
                                    <h3 className="font-bold text-gray-800 truncate text-lg">{staff.name}</h3>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${currentConfig.bg} ${currentConfig.color}`}>
                                            {staff.is_internal ? 'INTERNAL' : 'MANAGED'}
                                        </span>
                                        {!staff.is_active && <span className="text-xs bg-red-100 text-red-600 px-2 rounded font-bold">SUSPENDED</span>}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="text-sm text-gray-600 space-y-2 border-t border-gray-100 pt-3">
                                <p className="flex items-center gap-2"><FaPhone size={12} className="text-gray-400 flex-shrink-0" /> {staff.phone}</p>
                                <p className="flex items-center gap-2"><FaMapMarkerAlt size={12} className="text-gray-400 flex-shrink-0" /> {getLgaName(staff.location_lga_id)}</p>
                                <p className="text-xs text-gray-400 truncate pl-5">{staff.address}</p>
                            </div>
                            
                            {/* --- ACTION BUTTONS (EDIT & DELETE) --- */}
                            <div className="absolute top-4 right-4 flex gap-2">
                                <button 
                                    // onClick={() => handleEditStaff(staff)} // Edit logic not implemented yet
                                    className="text-gray-300 hover:text-blue-600 transition-colors p-1"
                                    title="Edit Entity"
                                >
                                    <FaEdit />
                                </button>
                                <button 
                                    onClick={() => handleDeleteStaff(staff.id)}
                                    className="text-gray-300 hover:text-red-500 transition-colors p-1"
                                    title="Remove Entity"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                    
                    {/* Empty State */}
                    {internalStaff.filter(s => s.type === activeTab).length === 0 && (
                        <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                            <currentConfig.icon className="mx-auto text-4xl text-gray-300 mb-3" />
                            <p className="text-gray-500 font-medium mb-4">{currentConfig.emptyMsg}</p>
                            <button 
                                onClick={() => handleOpenAddModal(activeTab)}
                                className={`font-bold hover:underline ${currentConfig.color}`}
                            >
                                {currentConfig.addButton}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* --- ADD/EDIT STAFF MODAL --- */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh] animate-fadeInUp">
                        
                        {/* Header (Fixed) */}
                        <div className={`flex justify-between items-center p-6 border-b pb-2 flex-shrink-0 ${TAB_CONFIG[formData.type].bg}`}>
                            <div>
                                <h3 className={`text-lg font-bold ${TAB_CONFIG[formData.type].color}`}>
                                    {editingId ? `Edit ${TAB_CONFIG[formData.type].singular}` : `Register New ${TAB_CONFIG[formData.type].singular}`}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    This creates a User Account and Provider Profile instantly.
                                </p>
                            </div>
                            <button onClick={() => setIsAddModalOpen(false)} className="text-gray-500 hover:text-gray-700"><FaTimes /></button>
                        </div>
                        
                        {/* Body (Scrollable) */}
                        <form onSubmit={handleAddOrUpdateStaff} className="p-6 space-y-5 overflow-y-auto">
                            
                            {/* --- USER ACCOUNT DETAILS --- */}
                            <div className="bg-blue-100 p-4 rounded-lg border border-blue-200">
                                <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                                    <FaUserPlus /> Manager/Owner Account
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Full Name *</label>
                                        <input name="name" value={formData.name} onChange={handleInputChange} required className="w-full p-2 border rounded" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Password *</label>
                                        <input type="password" name="new_manager_password" value={formData.new_manager_password} onChange={handleInputChange} required minLength={6} className="w-full p-2 border rounded" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Email *</label>
                                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full p-2 border rounded" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Phone *</label>
                                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full p-2 border rounded" />
                                    </div>
                                </div>
                                
                                {/* --- ADMIN AUTO-FILL BUTTON --- */}
                                {user && (
                                    <button 
                                        type="button"
                                        onClick={handleLoadAdminProfile}
                                        className="mt-3 w-full bg-gray-200 text-gray-700 px-3 py-2 rounded text-xs font-bold hover:bg-gray-300 flex items-center justify-center gap-2"
                                    >
                                        <FaUserShield /> Load My Admin Profile
                                    </button>
                                )}
                            </div>

                            {/* --- BUSINESS DETAILS --- */}
                            <div className="space-y-4">
                                <h4 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Business/Branch Details</h4>
                                
                                {/* Store Name (Only for Stores) */}
                                {['parts_seller', 'car_dealer'].includes(formData.type) && (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Store/Showroom Name *</label>
                                        
                                        {/* --- HYBRID INPUT/DROPDOWN --- */}
                                        <select 
                                            name="store_name_select" 
                                            value={isCustomStoreName ? 'CUSTOM_ENTRY' : formData.store_name} 
                                            onChange={handleInputChange} 
                                            required 
                                            className="w-full p-2 border rounded bg-white mb-2"
                                        >
                                            <option value={EXCLUSIVE_STORE_NAME}>{EXCLUSIVE_STORE_NAME}</option>
                                            <option value="CUSTOM_ENTRY">-- Enter New Store Name --</option>
                                        </select>

                                        {isCustomStoreName && (
                                            <input 
                                                type="text" 
                                                name="store_name" 
                                                value={formData.store_name} 
                                                onChange={handleInputChange} 
                                                required 
                                                placeholder="Enter Partner Store Name"
                                                className="w-full p-2 border rounded border-blue-300 focus:ring-2 focus:ring-blue-500" 
                                            />
                                        )}
                                        {/* ----------------------------- */}
                                    </div>
                                )}

                                {/* Internal / Managed Toggle (Only for Stores) */}
                                {formData.type === 'parts_seller' && (
                                    <label className="flex items-center gap-3 p-3 rounded bg-green-50 cursor-pointer border border-green-200">
                                        <input 
                                            type="checkbox" 
                                            name="is_managed_by_ea" 
                                            checked={formData.is_managed_by_ea} 
                                            onChange={handleInputChange}
                                            className="h-5 w-5 text-green-600 rounded" 
                                        />
                                        <div>
                                            <span className="font-bold text-sm text-green-800">Managed by EA Agency</span>
                                            <p className="text-xs text-green-700">Check this if this is a partner store you are running on their behalf.</p>
                                        </div>
                                    </label>
                                )}

                                {/* Location */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">State *</label>
                                        <select name="state" value={formData.state} onChange={handleInputChange} required className="w-full p-2 border rounded bg-white">
                                            <option value="">Select State</option>
                                            {Object.keys(groupedLgas).sort().map(state => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">LGA *</label>
                                        <select name="lga_id" value={formData.lga_id} onChange={handleInputChange} required disabled={!formData.state} className="w-full p-2 border rounded bg-white disabled:bg-gray-100">
                                            <option value="">Select LGA</option>
                                            {availableLgas.map(l => (
                                                <option key={l.id} value={l.id}>{l.lga}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Address</label>
                                    <input name="address" value={formData.address} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="e.g. 5 Adejuwon Street" />
                                </div>
                                
                                {/* Skills (For Service Providers & Logistics) */}
                                {['station', 'freelancer', 'expert', 'logistics_partner'].includes(formData.type) && (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Skills (Comma separated)</label>
                                        <input name="skills" value={formData.skills} onChange={handleInputChange} placeholder="e.g. Painting, BMW Engine" className="w-full p-2 border rounded" />
                                    </div>
                                )}
                            </div>

                            {/* --- FINANCIALS (Only for Stores) --- */}
                            {formData.type === 'parts_seller' && (
                                <div className="bg-green-50 p-4 rounded border border-green-200">
                                    <h4 className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
                                        <FaUniversity /> Payout Details (Optional)
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Bank Name</label>
                                            
                                            {/* --- NEW SEARCHABLE BANK DROPDOWN --- */}
                                            <div className="relative" ref={dropdownRef}>
                                                <div 
                                                    className="w-full p-2 border rounded bg-white flex justify-between items-center cursor-pointer"
                                                    onClick={() => setIsBankListOpen(!isBankListOpen)}
                                                >
                                                    <span className={formData.bank_code ? "text-gray-800" : "text-gray-400"}>
                                                        {getBankName(formData.bank_code) || "Select Bank"}
                                                    </span>
                                                    <FaChevronDown className="text-gray-400 text-xs" />
                                                </div>

                                                {isBankListOpen && (
                                                    <div className="absolute z-50 w-full bg-white border border-gray-300 rounded shadow-lg mt-1 max-h-40 overflow-hidden flex flex-col">
                                                        <div className="p-2 border-b bg-gray-50">
                                                            <div className="flex items-center bg-white border rounded px-2">
                                                                <FaSearch className="text-gray-400" />
                                                                <input 
                                                                    type="text"
                                                                    className="w-full p-2 outline-none text-sm"
                                                                    placeholder="Search bank..."
                                                                    value={bankSearchTerm}
                                                                    onChange={(e) => setBankSearchTerm(e.target.value)}
                                                                    autoFocus
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="overflow-y-auto flex-grow">
                                                            {filteredBanks.length > 0 ? (
                                                                filteredBanks.map(bank => (
                                                                    <div 
                                                                        key={bank.code}
                                                                        onClick={() => handleSelectBank(bank)}
                                                                        className="p-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 border-b border-gray-50 last:border-0"
                                                                    >
                                                                        {bank.name}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="p-4 text-center text-gray-500 text-sm">No banks found.</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {/* ------------------------------------------ */}
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Account Number</label>
                                            <input name="account_number" value={formData.account_number} onChange={handleInputChange} className="p-2 border rounded w-full" placeholder="10-digit NUBAN" maxLength={10} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                                <button type="submit" disabled={submitting} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">
                                    {submitting ? 'Saving...' : `Register ${currentConfig.singular}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- SCROLL TO TOP BUTTON --- */}
            <button
                onClick={handleScrollToTop}
                className={`fixed bottom-6 right-6 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 z-50 ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}
                title="Scroll to Top"
            >
                <FaArrowUp />
            </button>

        </div>
    );
}

export default AdminEAHub;